/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';
import Loadable from 'react-loadable';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { jsx } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import type { Placement } from '@atlaskit/dropdown-menu/types';
import AddIcon from '@atlaskit/icon/core/add';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkIcon from '@atlaskit/icon/core/link';
import MentionIcon from '@atlaskit/icon/core/mention';
import PageIcon from '@atlaskit/icon/core/page';
import Tooltip from '@atlaskit/tooltip';
import {
	type ExternalContextBrowserProps,
	type PromptInputContext,
	type PromptInputContextMenuItem,
	useExternalContextBrowser,
} from '@atlassian/conversation-assistant-external-context';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';
import {
	type Agent,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service';

import { useConversationExternalAttachments } from '../../controllers/conversation-external-attachments';
import { useConversationFiles } from '../../controllers/conversation-files';
import mergeRefs from '../../utils/merge-refs';

import { useCurrentPageAsContext } from './current-page-as-context';
import { FileUploadDropdownItem } from './file-upload-dropdown-item';
import { messages } from './messages';

const AgentSection = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_prompt-agent-section" */ './agent-section'
			)
		).AgentSection,
	loading: () => null,
});

const RecommendedAgentsPreloader = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_prompt-recommended-agents-preloader" */ './agent-section/recommended-agents-preloader'
			)
		).RecommendedAgentsPreloader,
	loading: () => null,
});

type ChatInputActionButtonsProps = {
	siteId: string;
	useCurrentPageContext: boolean;
	onLinkTypeAheadClick: () => void;
	onMentionTypeAheadClick: () => void;
	onAddTypeAheadClick: () => void;
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
	isDisabled: boolean;
	placement?: Placement;
	attachmentsAndCurrentPageAsContextStoreKey: string;
	onMenuOpen?: () => void;
	onAgentSelect?: (agent: Agent) => void;
	shouldRenderToParent?: boolean;
	isBrowseAgentsModalOpen?: boolean;
	setIsBrowseAgentsModalOpen?: (isBrowseAgentsModalOpen: boolean) => void;
} & ExternalContextBrowserProps;

export const ChatInputActionButtons = ({
	siteId,
	useCurrentPageContext,
	onLinkTypeAheadClick,
	onMentionTypeAheadClick,
	onAddTypeAheadClick,
	assistanceServiceParams,
	contextProviders,
	onContextBrowserShow,
	onContextAvailable,
	isDisabled,
	placement = 'top-start',
	attachmentsAndCurrentPageAsContextStoreKey: attachmentsStoreAndCurrentPageContextKey,
	onMenuOpen,
	onAgentSelect,
	shouldRenderToParent,
	isBrowseAgentsModalOpen = false,
	setIsBrowseAgentsModalOpen = () => {},
}: ChatInputActionButtonsProps) => {
	di(useCurrentPageAsContext, useConversationFiles);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { setUseCurrentPageContextUserPreference, currentContext } = useCurrentPageAsContext();
	const showAddContextButton = useCurrentPageContext;

	const [menuState, setMenuState] = useState<'main' | 'external-context'>('main');
	const [currentContextBrowser, setCurrentContextBrowser] = useState<ReactNode | null>(null);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const { addExternalContext } = useConversationExternalAttachments(
		attachmentsStoreAndCurrentPageContextKey,
	);
	const returnFocusRef = useRef<HTMLElement>(null);

	const inputRef = useRef<HTMLInputElement>(null);
	const { getFileInput } = useConversationFiles({
		assistanceServiceParams: {
			...assistanceServiceParams,
			touchpointSource: 'chatInputActionButtons',
		},
		key: attachmentsStoreAndCurrentPageContextKey,
	});

	const { getMenuItems } = useExternalContextBrowser({
		contextProviders,
		onContextBrowserShow: (browser: ReactNode) => {
			if (browser) {
				setCurrentContextBrowser(browser);
				setMenuState('external-context');
			} else {
				setCurrentContextBrowser(null);
				setMenuState('main');
			}
			onContextBrowserShow?.(browser);
		},
		onContextAvailable: (contexts: PromptInputContext[]) => {
			setMenuState('main');
			setCurrentContextBrowser(null);
			setIsDropdownOpen(false);
			// Add external contexts to unified store
			addExternalContext(attachmentsStoreAndCurrentPageContextKey, contexts);
			onContextAvailable?.(contexts);
		},
	});

	const focusOnClose = (
		event?: Event | React.MouseEvent | React.KeyboardEvent | KeyboardEvent | null | undefined,
	) => {
		// Refocus on the trigger when closing the popup except for native mouse events
		if (!event || (event as MouseEvent).detail === 0) {
			requestAnimationFrame(() => {
				returnFocusRef?.current?.focus();
			});
		}
	};

	const externalMenuItems = getMenuItems();

	return (
		<>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, onClick, ...props }) => {
					const icon = props.isSelected ? CrossIcon : AddIcon;
					return (
						<IconButton
							{...props}
							onClick={(e, analyticsEvent) => {
								sendAnalyticsEvent(
									analyticsEvent.update({
										actionSubjectId: 'rovoChatAddMenuTrigger',
									}),
								);
								onClick?.(e);
							}}
							isTooltipDisabled={false}
							appearance="subtle"
							shape="circle"
							icon={icon}
							label={formatMessage(messages.chatInputActionsButtonsTooltip)}
							ref={mergeRefs([triggerRef, returnFocusRef])}
							isDisabled={isDisabled}
						/>
					);
				}}
				// It fixes the issue of editor popup flashes / does not appear in Safari
				returnFocusRef={{ current: null }}
				placement={placement}
				spacing="compact"
				isOpen={isDropdownOpen}
				onOpenChange={(attrs) => {
					setIsDropdownOpen(attrs.isOpen);
					if (attrs.isOpen) {
						onMenuOpen?.();
					} else {
						setMenuState('main');
						setCurrentContextBrowser(null);
						focusOnClose(attrs.event);
					}
				}}
				shouldFlip
				shouldRenderToParent={shouldRenderToParent}
			>
				<DropdownItemGroup>
					{menuState === 'main' && (
						<>
							<FileUploadDropdownItem
								attachmentsStoreKey={attachmentsStoreAndCurrentPageContextKey}
								onClick={() => inputRef.current?.click()}
								assistanceServiceParams={assistanceServiceParams}
							/>
							<DropdownItem
								elemBefore={<LinkIcon label="" />}
								onClick={() => {
									onLinkTypeAheadClick();
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'rovoChatAddLinkButton',
									});
								}}
							>
								{formatMessage(messages.linkTypeAheadButtonLabel)}
							</DropdownItem>
							{externalMenuItems.map((item: PromptInputContextMenuItem) => (
								<DropdownItem
									key={item.key}
									elemBefore={item.icon}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										item.action();
										sendAnalyticsEvent({
											action: 'clicked',
											actionSubject: 'button',
											actionSubjectId: `rovoChatExternalContext_${item.key}`,
										});
									}}
								>
									{item.label}
								</DropdownItem>
							))}
							<DropdownItem
								elemBefore={<MentionIcon label="" />}
								onClick={() => {
									onMentionTypeAheadClick();
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'rovoChatMentionButton',
									});
								}}
							>
								{formatMessage(messages.mentionTypeAheadButtonLabel)}
							</DropdownItem>
							<DropdownItem
								elemBefore={<AddIcon label="" />}
								onClick={() => {
									onAddTypeAheadClick();
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'rovoChatAddFormattingButton',
									});
								}}
							>
								{formatMessage(messages.addMoreFormattingButtonLabel)}
							</DropdownItem>
							{showAddContextButton && (
								<Tooltip
									position="top"
									content={formatMessage(messages.addCurrentPageAsContextDisabledTooltip)}
									canAppear={() => currentContext.type === 'none'}
								>
									<DropdownItem
										isDisabled={currentContext.type === 'none'}
										elemBefore={<PageIcon label="" />}
										onClick={() => {
											setUseCurrentPageContextUserPreference(
												attachmentsStoreAndCurrentPageContextKey,
												true,
											);
											sendAnalyticsEvent({
												action: 'clicked',
												actionSubject: 'button',
												actionSubjectId: 'rovoChatAddContextButton',
											});
										}}
									>
										{formatMessage(messages.addContentButtonLabel)}
									</DropdownItem>
								</Tooltip>
							)}
						</>
					)}
					{menuState === 'external-context' && currentContextBrowser && (
						<>{currentContextBrowser}</>
					)}
				</DropdownItemGroup>
				{onAgentSelect && (
					<Suspense>
						<AgentSection
							assistanceServiceParams={assistanceServiceParams}
							onAgentSelect={onAgentSelect}
							siteId={siteId}
							isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
							setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
							setIsDropdownOpen={setIsDropdownOpen}
						/>
					</Suspense>
				)}
			</DropdownMenu>
			{getFileInput(inputRef)}
			{onAgentSelect && <AgentSectionPreloader assistanceServiceParams={assistanceServiceParams} />}
		</>
	);
};

const AgentSectionPreloader = ({
	assistanceServiceParams,
}: {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
}) => {
	// Preload the bundle
	useEffect(() => {
		AgentSection.preload();
	}, []);

	// Preload the data
	return <RecommendedAgentsPreloader assistanceServiceParams={assistanceServiceParams} />;
};
